// PaymentMethod.js 
import React, { useState, useRef, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import { formatCurrency } from '../utils/currencyFormatter';

const stripePromise = loadStripe("pk_test_51Kj1HUL5HUd0pNaueW1MnSOBmacgFG8Y1d5RaaC96ww5salv4xhj6gtrqeD6FtBCJJABx8J1oMZjgjYoKsDaZbsV00b8Ss5n6Y");

const focusEl = (el) => { try { el && typeof el.focus === 'function' && el.focus(); } catch {} };

const CheckoutForm = ({
  totalAmount,
  couponDiscount,
  onPaymentSuccess,
  loading,
  expandCardDetails,
  // NEW: notify parent that the attempt has finished (success OR error)
  onPaymentFinished
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef();

  useEffect(() => {
    if (!expandCardDetails) return;
    const handler = (e) => {
      if (e.detail === 'submit-card-form' && formRef.current && !isSubmitting) {
        formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    };
    window.addEventListener('cardFormEvent', handler);
    return () => window.removeEventListener('cardFormEvent', handler);
  }, [expandCardDetails, isSubmitting]);

  useEffect(() => {
    if (!expandCardDetails || !elements) return;
    focusEl(elements.getElement(CardNumberElement));
  }, [expandCardDetails, elements]);

  // Clear visible error as user edits any of the card fields (nice UX)
  useEffect(() => {
    if (!elements) return;
    const n = elements.getElement(CardNumberElement);
    const e = elements.getElement(CardExpiryElement);
    const c = elements.getElement(CardCvcElement);
    const clear = () => setError(null);
    n && n.on && n.on('change', clear);
    e && e.on && e.on('change', clear);
    c && c.on && c.on('change', clear);
    return () => {
      n && n.off && n.off('change', clear);
      e && e.off && e.off('change', clear);
      c && c.off && c.off('change', clear);
    };
  }, [elements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setProcessing(true);
    setError(null);

    if (!stripe || !elements) {
      setProcessing(false);
      setIsSubmitting(false);
      setError('Payment is unavailable right now. Please try again.');
      // ensure parent button resets if it was set
      onPaymentFinished && onPaymentFinished();
      return;
    }

    try {
      const finalAmount = Math.round((totalAmount - couponDiscount) * 100);
      if (finalAmount < 50) throw new Error('Amount must be at least $0.50');
      if (finalAmount > 99999999) throw new Error('Amount exceeds maximum allowed');

      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/create-payment-intent`, {
        amount: finalAmount,
        currency: 'aud',
        capture_method: 'manual',
        idempotencyKey: `payment-${Date.now()}`
      });

      const numberEl = elements.getElement(CardNumberElement);

      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: numberEl,
            billing_details: { name: 'Customer Name' }
          }
        }
      );

      if (confirmError) throw confirmError;

      const pmRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-payment-method/${paymentIntent.payment_method}`
      );
      const pm = pmRes.data;
      if (!pm || !pm.card) throw new Error('Payment method details not available');

      const paymentDetails = {
        paymentIntentId: paymentIntent.id,
        paymentMethodId: paymentIntent.payment_method,
        cardBrand: pm.card.brand,
        cardLast4: pm.card.last4,
        cardExpMonth: String(pm.card.exp_month),
        cardExpYear: String(pm.card.exp_year),
        amount: paymentIntent.amount,
        currency: paymentIntent.currency,
        refunded: false,
        refundAmount: 0
      };

      onPaymentSuccess({ paymentMethod: 'card', paymentDetails });
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
      setIsSubmitting(false);
      // ALWAYS tell parent to release its processing state
      onPaymentFinished && onPaymentFinished();
    }
  };

  const elementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': { color: '#aab7c4' }
      },
      invalid: { color: '#9e2146' }
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="credit-card-details"
      style={{ marginBottom: 0, display: expandCardDetails ? 'block' : 'none' }}
    >
      <div className="card-detail-row">
        <label className="detail-label">Card Number</label>

        <div
          className="card-number-input-container"
          style={{ position: 'relative' }}
          onClick={() => focusEl(elements?.getElement(CardNumberElement))}
        >
          <div className="form-control detail-input card-number-input" style={{ paddingRight: 44 }}>
            <CardNumberElement
              options={elementOptions}
              className="stripe-input"
            />
          </div>
          <i
            className="far fa-credit-card card-type-icon"
            style={{ pointerEvents: 'none' }}
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="card-detail-row two-column">
        <div className="detail-column">
          <label className="detail-label">Expires</label>
          <div
            className="card-number-input-container"
            onClick={() => focusEl(elements?.getElement(CardExpiryElement))}
          >
            <div className="form-control detail-input">
              <CardExpiryElement options={elementOptions} className="stripe-input" />
            </div>
          </div>
        </div>

        <div className="detail-column">
          <label className="detail-label">CVV/CVC</label>
          <div
            className="cvv-input-container"
            onClick={() => focusEl(elements?.getElement(CardCvcElement))}
          >
            <div className="form-control detail-input cvv-input" style={{ paddingRight: 34 }}>
              <CardCvcElement options={elementOptions} className="stripe-input" />
            </div>
            <span className="cvv-info" title="The 3–4 digit code on your card" style={{ pointerEvents: 'none' }}>
              ?
            </span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger mt-2">{error}</div>}
    </form>
  );
};

const PaymentMethod = ({ context }) => {
  const {
    totalPrice,
    returnTotalPrice,
    isReturnBooking,
    handleBookingConfirmation,
    loading,
    couponCode,
    setCouponCode,
    couponDiscount,
    setCouponDiscount
  } = context;

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [couponInput, setCouponInput] = useState(couponCode || '');
  const [couponLoading, setCouponLoading] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const totalAmount = totalPrice + (isReturnBooking ? returnTotalPrice : 0);
  const grandTotal = totalAmount - couponDiscount;

  const handleCouponValidation = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setPaymentError(null);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/validate-coupon`, {
        code: couponInput.trim()
      });
      if (res.data.error) throw new Error(res.data.error);
      setCouponCode(couponInput.trim());
      setCouponDiscount(res.data.discount);
    } catch (err) {
      setCouponInput('');
      setCouponDiscount(0);
      setPaymentError(err.response?.data?.error || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleMethodClick = (method) => {
    if (isProcessingPayment) return;
    if (method !== 'card') return;
    setPaymentMethod(method);
    setPaymentError(null);
    if (method === 'card') {
      setTimeout(() => {
        const el = document.getElementById('creditCardDetails');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  };

  const handleFooterPay = async () => {
    if (isProcessingPayment) return;
    if (!paymentMethod) {
      setPaymentError('Please select a payment method');
      return;
    }
    setIsProcessingPayment(true);
    setPaymentError(null);
    try {
      if (paymentMethod === 'card') {
        window.dispatchEvent(new CustomEvent('cardFormEvent', { detail: 'submit-card-form' }));
      }
    } catch (err) {
      setPaymentError(err.message || 'Payment processing failed');
      setIsProcessingPayment(false);
    }
  };

  const payLabel =
    paymentMethod === 'card'
      ? `PAY ${formatCurrency(grandTotal)}`
      : 'SELECT PAYMENT METHOD';

  if (!stripePromise) {
    return (
      <div className="addon-sheet">
        <div className="alert alert-danger">
          Stripe publishable key is missing. Set <code>REACT_APP_STRIPE_PUBLIC_KEY</code> in your .env
          or keep the fallback key in code.
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="payment-method-container">
        <div className="addon-sheet">
          <div className="promo-code-container">
            <div className="promo-code-input flex-grow-1">
              <input
                type="text"
                className="form-control"
                placeholder="Promo Code"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
                disabled={couponLoading}
              />
            </div>
            <button
              className="apply-btn"
              style={{ marginLeft: 12, minWidth: 90 }}
              type="button"
              onClick={handleCouponValidation}
              disabled={!couponInput.trim() || couponLoading}
            >
              {couponLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
              Apply
            </button>
          </div>
          {couponDiscount > 0 && (
            <div className="text-success small" style={{ marginLeft: 2 }}>
              Coupon applied: ${couponDiscount} discount
            </div>
          )}
          {paymentError && couponInput && <div className="alert alert-danger mt-2">{paymentError}</div>}
        </div>

        <div className="addon-sheet" style={{ marginTop: 0 }}>
          <div className="payment-methods-title mb-3">PAYMENT METHODS</div>
          <div className="payment-methods-container">
            <div
              className={`payment-method${paymentMethod === 'card' ? ' selected' : ''}`}
              style={{
                cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                opacity: isProcessingPayment ? 0.7 : 1
              }}
              onClick={() => !isProcessingPayment && handleMethodClick('card')}
              id="creditCardMethod"
            >
              <div className="method-icon">
                <i className="far fa-credit-card"></i>
              </div>
              <div className="method-details">
                <div className="method-name">Credit/Debit Card</div>
                <div className="method-description">Visa, Mastercard, Amex</div>
              </div>
              <div className="method-radio">
                <i className={paymentMethod === 'card' ? 'fas fa-check-circle' : 'far fa-circle'}></i>
              </div>
            </div>

            <div
              id="creditCardDetails"
              style={{
                transition: 'all 0.25s',
                maxHeight: paymentMethod === 'card' ? '450px' : '0',
                overflow: 'hidden'
              }}
            >
              <CheckoutForm
                totalAmount={totalAmount}
                couponDiscount={couponDiscount}
                onPaymentSuccess={(data) => {
                  setIsProcessingPayment(false);
                  context.handleBookingConfirmation(data);
                }}
                loading={loading}
                expandCardDetails={paymentMethod === 'card'}
                // NEW: always release the parent spinner after an attempt
                onPaymentFinished={() => setIsProcessingPayment(false)}
              />
            </div>
          </div>

          {paymentError && !couponInput && <div className="alert alert-danger mt-3">{paymentError}</div>}
        </div>

        <div className="footer-bar">
          <button
            className="back-btn"
            onClick={context.handleBack}
            disabled={isProcessingPayment}
          >
            <FaArrowLeft />
          </button>
          <button
            className={`btn-proceed ${!paymentMethod ? 'disabled' : ''}`}
            style={{ minWidth: 180 }}
            onClick={handleFooterPay}
            disabled={loading || isProcessingPayment || !stripePromise}
          >
            {loading || isProcessingPayment ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Processing...
              </>
            ) : (
              payLabel
            )}
          </button>
        </div>
      </div>
    </Elements>
  );
};

export default PaymentMethod;